import type { CustomCellEditorProps } from 'ag-grid-react';
import { useGridCellEditor } from 'ag-grid-react';
import { get } from 'lodash-es';
import { memo, useCallback, useRef, useState } from 'react';
import { useSyncedRef } from '../../hooks/useSyncedRef';
import { isGridApiReady } from '../BlotterTable/utils';
import { Input } from '../Form/Input';
import { agGridStaticCellClassIsRightAligned } from './agGridStaticCellClass';

export type AgGridInputLiveEditParams = {
  /** Passed to `node.setDataValue` on each change (e.g. for `onCellEditRequest` `source`). */
  source: string;
  /** `source` when the user presses Escape (rollback). */
  escapeRollbackSource: string;
  /** `aria-label` on the input; defaults to `colDef.headerName` when the editor mounts. */
  ariaLabel?: string;
};

function toInputString(value: unknown): string {
  if (value == null) {
    return '';
  }
  return String(value);
}

/**
 * Text cell editor: commits each keystroke with `setDataValue` and the given `source`. Escape
 * rolls back to the value at edit start (including `null`) and uses `escapeRollbackSource`.
 * Enter stops editing after the last live edit.
 */
function AgGridInputLiveEditInner(props: CustomCellEditorProps<unknown, unknown> & AgGridInputLiveEditParams) {
  const inputRef = useRef<HTMLInputElement>(null);
  const propsRef = useSyncedRef(props);
  const valueAtEditStartRef = useRef<unknown | undefined>(undefined);
  const [local, setLocal] = useState(() => toInputString(props.initialValue));
  const { onKeyDown } = props;
  const { source, ariaLabel, node, colDef } = props;

  const forwardKeyDown = useCallback(
    (nativeEvent: KeyboardEvent) => {
      onKeyDown(nativeEvent);
    },
    [onKeyDown]
  );

  useGridCellEditor({
    afterGuiAttached() {
      const p = propsRef.current;
      const field = p.colDef?.field;
      if (field && p.node?.data != null) {
        valueAtEditStartRef.current = get(p.node.data, field);
      } else {
        valueAtEditStartRef.current = p.initialValue;
      }
      inputRef.current?.focus();
      if (p.cellStartedEdit) {
        inputRef.current?.select();
      }
    },
    focusIn() {
      inputRef.current?.focus();
    },
  } as Parameters<typeof useGridCellEditor>[0]);

  const isRightAligned = agGridStaticCellClassIsRightAligned(colDef.cellClass);
  const label = ariaLabel ?? props.colDef?.headerName?.toString() ?? '';

  return (
    <Input
      ref={inputRef}
      aria-label={label}
      value={local}
      type={isRightAligned ? 'number' : 'text'}
      onChange={event => {
        const next = event.target.value;
        setLocal(next);
        props.onValueChange(next);
        props.node.setDataValue(props.column, next, source);
      }}
      onKeyDownCapture={event => {
        if (event.key === 'Escape') {
          const p = propsRef.current;
          const valueAtEditStart = valueAtEditStartRef.current;
          const revert = valueAtEditStart === undefined ? p.initialValue : valueAtEditStart;
          setLocal(toInputString(revert));
          p.onValueChange(revert);
          p.node.setDataValue(p.column, revert, p.escapeRollbackSource);
          event.preventDefault();
          event.stopPropagation();
          queueMicrotask(() => {
            if (!isGridApiReady(p.api)) {
              return;
            }
            p.api.stopEditing(true);
          });
          return;
        }
        if (event.key === 'Enter' || event.key === 'NumpadEnter') {
          if (event.nativeEvent.isComposing) {
            forwardKeyDown(event.nativeEvent);
            return;
          }
          const p = propsRef.current;
          event.preventDefault();
          event.stopPropagation();
          queueMicrotask(() => {
            if (!isGridApiReady(p.api)) {
              return;
            }
            p.api.stopEditing(false);
          });
          return;
        }
        forwardKeyDown(event.nativeEvent);
      }}
      style={{
        width: '100%',
        height: `${node.rowHeight}px`,
        background: props.context.current.theme?.backgroundBody,
      }}
    />
  );
}

export const AgGridInputLiveEdit = memo(AgGridInputLiveEditInner);
